/**
 * GradientCache - Caches radial gradients as offscreen canvases.
 * Instead of calling createRadialGradient() + arc() + fill() every frame,
 * we draw once to an offscreen canvas and stamp it with drawImage().
 */

interface CachedGradient {
  canvas: HTMLCanvasElement;
  zoom: number;
}

/** Create an offscreen canvas with a radial gradient circle pre-rendered. */
function createGradientCanvas(
  radius: number,
  stops: Array<{ offset: number; color: string }>,
  ellipseRatioY?: number
): HTMLCanvasElement {
  const size = Math.ceil(radius * 2);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = ellipseRatioY ? Math.ceil(size * ellipseRatioY) : size;
  const ctx = canvas.getContext('2d')!;

  const cx = size / 2;
  const cy = canvas.height / 2;

  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
  for (const s of stops) {
    grad.addColorStop(s.offset, s.color);
  }
  ctx.fillStyle = grad;

  if (ellipseRatioY && ellipseRatioY !== 1) {
    // Draw as ellipse by scaling
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(1, ellipseRatioY);
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  } else {
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  return canvas;
}

// --- Enemy Shadow Cache ---
let enemyShadowCache: CachedGradient | null = null;

export function getEnemyShadowCanvas(zoom: number): HTMLCanvasElement {
  if (enemyShadowCache && enemyShadowCache.zoom === zoom) {
    return enemyShadowCache.canvas;
  }

  const radiusX = 22 * zoom;
  const radiusY = 8 * zoom;
  const ratio = radiusY / radiusX;

  const canvas = document.createElement('canvas');
  const w = Math.ceil(radiusX * 2);
  const h = Math.ceil(radiusY * 2);
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;

  const cx = w / 2;
  const cy = h / 2;

  // Draw ellipse shadow using scale transform
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(1, ratio);
  const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, radiusX);
  grad.addColorStop(0, 'rgba(0, 0, 0, 0.5)');
  grad.addColorStop(0.7, 'rgba(0, 0, 0, 0.2)');
  grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(0, 0, radiusX, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  enemyShadowCache = { canvas, zoom };
  return canvas;
}

// --- Enemy Attack Indicator Cache ---
let enemyAttackCache: CachedGradient | null = null;

export function getEnemyAttackCanvas(zoom: number): HTMLCanvasElement {
  if (enemyAttackCache && enemyAttackCache.zoom === zoom) {
    return enemyAttackCache.canvas;
  }

  const radius = 12 * zoom;
  const canvas = createGradientCanvas(radius, [
    { offset: 0, color: 'rgba(255, 100, 100, 0.8)' },
    { offset: 0.5, color: 'rgba(255, 50, 50, 0.4)' },
    { offset: 1, color: 'rgba(255, 0, 0, 0)' },
  ]);

  enemyAttackCache = { canvas, zoom };
  return canvas;
}

// --- Torch Glow Cache ---
let torchGlowCache: CachedGradient | null = null;

export function getTorchGlowCanvas(zoom: number): HTMLCanvasElement {
  if (torchGlowCache && torchGlowCache.zoom === zoom) {
    return torchGlowCache.canvas;
  }

  const radius = 128 * zoom;
  const canvas = createGradientCanvas(radius, [
    { offset: 0, color: 'rgba(255, 150, 50, 0.08)' },
    { offset: 0.5, color: 'rgba(255, 120, 30, 0.04)' },
    { offset: 1, color: 'rgba(255, 100, 0, 0)' },
  ]);

  torchGlowCache = { canvas, zoom };
  return canvas;
}

// --- Fog Patch Cache ---
// Fog patches come in varying radii, so we cache by rounded radius buckets
const fogCacheMap = new Map<number, HTMLCanvasElement>();
let fogCacheZoom: number = 0;

export function getFogPatchCanvas(radius: number, _alpha: number, zoom: number): HTMLCanvasElement {
  // Round radius to nearest 50 to limit cache entries
  const bucket = Math.round(radius / 50) * 50 || 50;
  const key = bucket;

  // Invalidate all fog caches if zoom changes
  if (zoom !== fogCacheZoom) {
    fogCacheMap.clear();
    fogCacheZoom = zoom;
  }

  const cached = fogCacheMap.get(key);
  if (cached) return cached;

  // Use a fixed mid-level alpha for the cached version;
  // actual alpha will be applied via globalAlpha at draw time
  const canvas = createGradientCanvas(bucket, [
    { offset: 0, color: 'rgba(180, 180, 180, 1)' },
    { offset: 0.6, color: 'rgba(160, 160, 170, 0.5)' },
    { offset: 1, color: 'rgba(160, 160, 170, 0)' },
  ]);

  fogCacheMap.set(key, canvas);
  return canvas;
}

// --- Player Shadow Cache ---
let playerShadowCache: CachedGradient | null = null;

export function getPlayerShadowCanvas(zoom: number): HTMLCanvasElement {
  if (playerShadowCache && playerShadowCache.zoom === zoom) {
    return playerShadowCache.canvas;
  }

  const radiusX = 18 * zoom;
  const radiusY = 6 * zoom;

  const canvas = document.createElement('canvas');
  const w = Math.ceil(radiusX * 2) + 4; // slight padding for stretch
  const h = Math.ceil(radiusY * 2) + 2;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;

  const cx = w / 2;
  const cy = h / 2;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(1, radiusY / radiusX);
  const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, radiusX);
  grad.addColorStop(0, 'rgba(0,0,0,0.5)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(0, 0, radiusX, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  playerShadowCache = { canvas, zoom };
  return canvas;
}

/** Call when zoom changes to invalidate all caches */
export function invalidateGradientCaches(): void {
  enemyShadowCache = null;
  enemyAttackCache = null;
  torchGlowCache = null;
  playerShadowCache = null;
  fogCacheMap.clear();
}
